import React, { useState } from 'react';
import moment, { Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import ReactPlaceholder from 'react-placeholder';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { analyticsLogger, PageType, ReportType } from 'commons/src/analytics';
import { INSIGHT_CREATED_REPORT } from 'commons/src/analytics/AnalyticsEvents';
import PageWrapper from 'commons/src/components/containers/PageWrapper';
import Error from 'commons/src/components/errorComponents/Error';
import PageHeader from 'commons/src/components/headers/PageHeader';
import SectionHeader from 'commons/src/components/headers/SectionHeader';
import SubHeader from 'commons/src/components/headers/SubHeader';
import { IntercomAPI } from 'commons/src/components/Intercom';
import { mediumFormLoader } from 'commons/src/components/placeholder';
import { iaqInsightReportDevices, iaqInsightReportSensors } from 'commons/src/DeviceAndSensorLists';
import { SensorTypes } from 'commons/src/models/commonEnums';
import { fetchIaqInsightData } from '../../../actions/insightActions';
import { paths } from '../../../constants';
import { BuildingProps } from '../../../models/commonEnums';
import { Store } from '../../../reducers';
import { BusinessRequestType as RequestType } from '../../../reducers/BusinessRequestType';
import InsightMultipleDevices, { ReportData } from '../reportSelector/InsightMultipleDevices';
import styles from './IAQInsight2Page.module.scss';
import IAQInsight2ResultView from './IAQInsight2ResultView';

const IAQInsight2Page: React.FC = () => {
    const { t: txt } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const locations = useSelector((state: Store) => state.locations.locations);
    const locationsLoading = useSelector((state: Store) => state.locations.loading);
    const locationsError = useSelector((state: Store) => state.locations.error);
    const fetchReportDataLoading = useSelector(
        (state: Store) => state.requests[RequestType.FetchIaqInsightData]?.loading ?? false
    );
    const error = useSelector((state: Store) => state.requests[RequestType.FetchIaqInsightData]?.error);

    const optionalBuildingProps = [BuildingProps.floors, BuildingProps.buildingYear];

    const [reportStartTime, setReportStartTime] = useState<Moment | null>(null);
    const [reportEndTime, setReportEndTime] = useState<Moment | null>(null);
    const [displayResultSection, setDisplayResultSection] = useState(false);

    if (locationsError) {
        return <Error />;
    }

    const isLoadingLocations = locationsLoading && locations.length === 0;

    const numberOfDays =
        reportEndTime && reportStartTime && moment(reportEndTime).diff(moment(reportStartTime), 'days');
    const graphHidden = numberOfDays && numberOfDays > 31;

    const fetchReportData = (reportData: ReportData): void => {
        const { serialNumbers, from, to, sensors, includeLogo } = reportData;
        analyticsLogger(INSIGHT_CREATED_REPORT, {
            pageType: PageType.Insight,
            reportType: ReportType.IAQInsight,
            reportData,
        });

        IntercomAPI('trackEvent', 'generated-IAQ-insight-report', {
            serialNumbers,
            from,
            to,
            sensors,
            logoInReport: includeLogo,
        });
        dispatch(fetchIaqInsightData(reportData));
    };
    const hideGraphProps = {
        header: 'Insight.GraphWillBeHidden',
        subText: 'Insight.GraphHiddenAbove31Days',
        isHidden: !!graphHidden,
    };

    const sensorsNotToAutoSelect: SensorTypes[] = [SensorTypes.mold];

    const goBack = (): void => {
        navigate({ pathname: `/${paths.reports}` });
    };

    return (
        <>
            <PageHeader title={txt('Insight.IaqAnalysis2.0')} />
            <PageWrapper pageType="wide">
                <SubHeader onGoBack={goBack} backTitle="Reports.Reports" actionButtons={[]} />
            </PageWrapper>
            <PageWrapper pageType="wide" whiteBackground>
                <ReactPlaceholder ready={!isLoadingLocations} customPlaceholder={mediumFormLoader}>
                    <div className={styles.formContainer}>
                        <SectionHeader headerName="Insight.IaqAnalysisDescription" headerType={2} />
                        <InsightMultipleDevices
                            setDisplayResultSection={setDisplayResultSection}
                            fetchReportDataLoading={fetchReportDataLoading}
                            fetchReportData={fetchReportData}
                            reportStartTime={reportStartTime}
                            reportEndTime={reportEndTime}
                            setReportStartTime={setReportStartTime}
                            setReportEndTime={setReportEndTime}
                            possibleSensorTypes={iaqInsightReportSensors}
                            acceptedDeviceTypes={iaqInsightReportDevices}
                            minNumberOfDays={0}
                            maxNumberOfDays={182}
                            allowLogo
                            hideGraphProps={hideGraphProps}
                            optionalBuildingProps={optionalBuildingProps}
                            sensorNotToAutoSelect={sensorsNotToAutoSelect}
                            showAllSensors
                            hideSegmentProperties
                        />
                    </div>
                </ReactPlaceholder>
            </PageWrapper>
            {displayResultSection && !error && reportStartTime && reportEndTime && (
                <div className={styles.resultSection}>
                    <IAQInsight2ResultView from={reportStartTime} to={reportEndTime} />
                </div>
            )}
        </>
    );
};

export default IAQInsight2Page;
